import type React from 'react';
import { X } from 'lucide-react';
import type { PendingAttachment } from '../../../../lib/use-chat';
import type { WorkspaceFile } from '../../../../lib/workspace-files';
import { useTranslation } from '../../../../lib/i18n';
import { ScrollArea } from '../../ui';
import { useBasePath } from '../../../../lib/use-base-path';
import { extensionOf } from '../../../../lib/artifact-file';
import { fileTypeKind } from '../../../../lib/artifact-image';

// The composer's staged-attachment strip (Figma "File Type Assets" 14705:128433 + the
// mobile/desktop examples 14701:104085): a horizontally scrolling row of square file-type tiles —
// 40px in the small/mobile composer, 60px in the full-size chat. Each tile is the file's type
// artwork (an image preview for pictures, else the shared file-type icon) on a light-gray rounded
// square with a white circular × to remove it. The row scrolls when it outgrows the composer width.
export function AttachmentChipRow({
  attachments,
  onRemove,
  onRetry,
  size = 40,
}: {
  attachments: PendingAttachment[];
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
  size?: 40 | 60;
}) {
  return (
    <ScrollArea axis="horizontal" bar="hidden">
      <div style={{ ...ROW, gap: size === 60 ? 10 : 8 }}>
        {attachments.map((p) => (
          <AttachmentChip key={p.id} pending={p} onRemove={onRemove} onRetry={onRetry} size={size} />
        ))}
      </div>
    </ScrollArea>
  );
}

function AttachmentChip({
  pending,
  onRemove,
  onRetry,
  size,
}: {
  pending: PendingAttachment;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
  size: 40 | 60;
}) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const isImage = pending.file.type.startsWith('image/');
  const isVideo = pending.file.type.toLowerCase().startsWith('video/');
  const isUploading = pending.status === 'uploading';
  const isError = pending.status === 'error';
  const isRejected = isError && (pending.errorKind === 'unsupported' || pending.errorKind === 'too_large');
  const retryable = isError && !isRejected;
  // We set no type or size limit, so `unsupported` and `too_large` only come from WixMP refusing the file.
  // Its own reason (`pending.error`, read from the mint's body) is shown with them; there is no number
  // of ours to quote.
  const wixmpReason = pending.error ? `\n\n${pending.error}` : '';
  const tooltip =
    pending.errorKind === 'unsupported'
      ? `${pending.file.name}\n\n${t(
          isVideo ? 'chat.attachments.unsupportedVideoTooltip' : 'chat.attachments.unsupportedTooltip',
        )}${wixmpReason}`
      : pending.errorKind === 'too_large'
      ? `${pending.file.name}${wixmpReason}`
      : retryable
      ? `${pending.file.name}\n\n${t('common.retry')}`
      : pending.file.name;
  const kind = fileTypeKind(extensionOf({ name: pending.file.name, isApp: false } as WorkspaceFile));
  const iconH = Math.round(size * 0.8);
  const art =
    isImage && pending.previewUrl ? (
      <img src={pending.previewUrl} alt="" style={{ ...thumb, opacity: isUploading ? 0.4 : 1 }} />
    ) : (
      // The same file-type artwork a sent chip and the Files list draw; a pending upload has no
      // content yet, so the extension picks the icon (image preview handled above).
      <img
        src={`${basePath}/file-types/${kind}.png`}
        alt=""
        height={iconH}
        style={{ height: iconH, width: 'auto', display: 'block', opacity: isUploading ? 0.4 : 1 }}
      />
    );
  return (
    <div title={tooltip} style={tile(size, isError)}>
      {/* A retryable upload makes the whole tile a real, focusable button (keyboard: Tab + Enter/Space);
          otherwise the art is inert. The remove × is a SIBLING, never nested inside it. */}
      {retryable ? (
        <button
          type="button"
          onClick={() => onRetry(pending.id)}
          aria-label={`${pending.file.name} — ${t('common.retry')}`}
          style={retryFill}
        >
          {art}
        </button>
      ) : (
        art
      )}
      {isUploading && (
        <span aria-label={t('chat.attachments.uploading')} style={spinnerOverlay}>
          <span style={spinnerBadge}>
            <svg className="orion-spinner" width="14" height="14" viewBox="0 0 16 16" aria-hidden>
              <circle cx="8" cy="8" r="6" fill="none" stroke="rgba(var(--ink-rgb),0.18)" strokeWidth="2.2" />
              <path d="M8 2a6 6 0 0 1 6 6" fill="none" stroke="rgba(var(--ink-rgb),0.85)" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          </span>
        </span>
      )}
      <button
        type="button"
        aria-label={t('chat.attachments.remove')}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(pending.id);
        }}
        style={remove(size)}
      >
        <X size={size === 60 ? 14 : 10} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
      </button>
    </div>
  );
}

const ROW: React.CSSProperties = { display: 'flex', alignItems: 'flex-start' };

const tile = (size: number, isError: boolean): React.CSSProperties => ({
  position: 'relative',
  flexShrink: 0,
  boxSizing: 'border-box',
  width: size,
  height: size,
  borderRadius: 8,
  background: 'var(--ds-surface-light-gray)',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: isError ? '1px solid rgba(var(--ds-red-300-rgb), 0.85)' : 'none',
});

// The retryable tile's clickable surface: a real <button> filling the tile (keyboard-focusable),
// centering the art. The remove × sits above it as a sibling.
const retryFill: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
};

const thumb: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
  transition: 'opacity 0.15s ease',
};

const spinnerOverlay: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
};

const spinnerBadge: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  borderRadius: '50%',
  background: 'rgba(var(--surface-base-rgb),0.92)',
};

// The Figma "Dismiss" affordance: a white circular button (light hairline border) in the tile's
// top-right corner. Below the DS Icon Button's 26px floor, so it stays hand-rolled (see button-spec).
const remove = (size: number): React.CSSProperties => {
  const d = size === 60 ? 22 : 16;
  const inset = size === 60 ? 3 : 2;
  return {
    position: 'absolute',
    top: inset,
    insetInlineEnd: inset,
    width: d,
    height: d,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--ds-surface-sunken)',
    border: '0.6px solid var(--ds-border-light-default)',
    borderRadius: '50%',
    color: 'var(--sym-ink)',
    cursor: 'pointer',
  };
};
